/**
 * useSystemModuleActiveLayout — F12.N E1.T5b.
 *
 * Hook pobierający aktywny layout modułu systemowego w NOWYM kształcie
 * (post-F12.N, ADR-0028) gotowy do podania do `RecursiveFormRenderer`.
 *
 * Następca `useSystemModuleTabConfig` (F12.M Etap 4 NEW), który operował
 * na starym kształcie `layoutConfig.tabs[]`. Po pełnym refactor stron detail
 * (E1.T5b zakończone) stary hook można usunąć.
 *
 * Backwards-compat: backend (`/api/system-modules/:slug/layouts/active`) na
 * dziś zwraca raw `layoutConfig` z bazy — w okresie przed deployem E1.T7
 * (jednorazowy skrypt migracji) ten layout będzie w STARYM kształcie.
 * Hook detektuje stary kształt przez `isAlreadyNewShape()` i wywołuje
 * `migrateLayoutToRecursive()` lokalnie w pamięci. Po deploy E1.T7 wszystkie
 * layouty w bazie są w nowym kształcie → migracja w locie staje się no-op.
 *
 * Zwraca:
 *  - `layoutConfig` — zawsze w NOWYM kształcie (lub `null` gdy brak layoutu).
 *  - `myRoleId` — rola usera w aktywnej firmie (null = super-user lub brak firmy).
 *  - `isSuperUser` — bypass per-rola visibility.
 *  - `isLoading` — pierwszy fetch trwa.
 *  - `error` — błąd HTTP.
 *  - `previewDockEnabled` — preferencja tenanta „pokazuj dokowany panel podglądu"
 *    z aktywnego layoutu (modelowana jak `isDefault` — patrz PUT [layoutId]/route.ts).
 *
 * Stale time: 2 min (jak poprzedni hook).
 *
 * `options.documentTypeId` (T1.6, layouty-e1) — opcjonalny typ dokumentu
 * dorzucany jako `?documentTypeId=` do requestu; gdy typ ma przypięty layout,
 * backend zwraca go (w odpowiedzi `resolveStrategy === 'document_type'`, patrz
 * `docs/refactor/LAYOUTY-PER-TYP-DOKUMENTU.md`). Brak (`undefined`/`null`) →
 * URL i klucz zapytania bit-identyczne z zachowaniem sprzed T1.6.
 *
 * Wskazanie z pozycji Kreatora menu (kartoteka-layout-per-menu): wewnętrznie woła
 * `useActiveMenuItem()` (czyta `?mi=<id>` z URL, bez dodatkowego round-tripu — cache
 * już ciepły z Sidebara) i, gdy aktywna pozycja ma `viewRef` DLA TEGO `moduleSlug`,
 * dorzuca `?layoutId=<uuid>` — wygrywa nad P0/kaskadą backendu (w odpowiedzi
 * `resolveStrategy === 'menu_item'`; hook tego pola nie wystawia). Bezpieczne dla
 * wszystkich wywołujących: dopasowanie po `entitySlug` odfiltrowuje `?mi=` z
 * niedopasowanej strony/modułu, więc bez pozycji menu (albo z inną encją) URL i klucz
 * zapytania zostają bit-identyczne.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { type LayoutConfig } from '@/components/form/RecursiveFormRenderer';
import { useActiveMenuItem } from '@/hooks/useActiveMenuItem';
import { api } from '@/lib/api';
import { applyTabOverrides, type TabOverrides } from '@/lib/kartoteki/apply-tab-overrides';
import { isAlreadyNewShape, migrateLayoutToRecursive } from '@/lib/migrate-layout-to-recursive';
import { parseMenuViewRef } from '@/lib/module-templates/template-layouts';

interface ActiveLayoutResponse {
  layout: {
    id: string;
    moduleSlug: string;
    layoutType: 'form' | 'list';
    name: string;
    isDefault: boolean;
    previewDockEnabled: boolean;
    layoutConfig: unknown;
    listConfig: unknown;
    categoryId: string | null;
    roleIds: string[];
    sourceViewSlug: string | null;
    tabOverrides: TabOverrides | null;
    createdAt: string;
    updatedAt: string;
  } | null;
  /** Odpowiedź API (skąd serwer wziął układ) — hook jej nie wystawia: strony nie rozgałęziają się po strategii. */
  resolveStrategy: string;
  candidateCount: number;
  myRoleId: string | null;
  isSuperUser: boolean;
  hasCompanyContext: boolean;
}

export interface UseSystemModuleActiveLayoutResult {
  /** Layout w nowym kształcie (po ewentualnej migracji w locie). `null` jeśli moduł nie ma layoutu. */
  layoutConfig: LayoutConfig | null;
  /** Rola usera w aktywnej firmie. `null` dla super-usera lub gdy brak company context. */
  myRoleId: string | null;
  /** Czy user jest super-userem (bypassuje per-rola visibility). */
  isSuperUser: boolean;
  /** Pierwszy fetch trwa (przed wynikiem). */
  isLoading: boolean;
  /** Czy fetch zwrócił błąd. */
  isError: boolean;
  /** Marker POCHODZENIA layoutu (zmaterializowany z centralnego widoku o tym slugu) — NIE świeżości. `null` = layout ręczny lub brak layoutu. */
  sourceViewSlug: string | null;
  /** Preferencja tenanta: pokazuj dokowany panel podglądu. `false` gdy brak layoutu. */
  previewDockEnabled: boolean;
}

export function useSystemModuleActiveLayout(
  moduleSlug: string,
  options?: { enabled?: boolean; documentTypeId?: string | null },
): UseSystemModuleActiveLayoutResult {
  // T1.6 (layouty-e1): typ dokumentu opcjonalnie zawęża rozstrzygnięcie layoutu
  // (backend: `?documentTypeId=` → jeśli typ wskazuje layout, wygrywa nad
  // kaskadą standardową). Puste stringi traktujemy jak brak — `undefined`
  // (nie podano) i `null` (podano jawnie, ale brak wartości, np. rekord jeszcze
  // się ładuje) muszą hashować się identycznie: segment klucza dokładamy
  // TYLKO gdy typ faktycznie jest znany, więc bez niego URL i klucz zostają
  // bit-identyczne ze stanem sprzed T1.6 dla wszystkich dotychczasowych callerów.
  const documentTypeId = options?.documentTypeId || null;

  // Pozycja menu tenanta, przez którą wszedł klient (`?mi=<id>`, kartoteka-layout-per-menu).
  // Gdy ma przypisany layout formularza (`viewRef`) DLA TEGO modułu — wymuszamy go jako
  // najwyższy priorytet (wygrywa nad P0/kaskadą backendu). Niedopasowany entitySlug (inna
  // strona/inny moduł) → `menuItemLayoutId=null` → zachowanie bit-identyczne ze stanem
  // sprzed tej zmiany.
  const activeMenuItem = useActiveMenuItem();
  const menuItemLayoutId = useMemo(() => {
    if (!activeMenuItem?.viewRef) return null;
    const parsed = parseMenuViewRef(activeMenuItem.viewRef);
    if (!parsed || parsed.entitySlug !== moduleSlug) return null;
    return parsed.viewSlug;
  }, [activeMenuItem?.viewRef, moduleSlug]);

  const query = useQuery({
    queryKey: [
      'system-module-layout-active',
      moduleSlug,
      ...(menuItemLayoutId ? [menuItemLayoutId] : []),
      ...(documentTypeId ? [documentTypeId] : []),
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (menuItemLayoutId) params.set('layoutId', menuItemLayoutId);
      if (documentTypeId) params.set('documentTypeId', documentTypeId);
      const qs = params.toString();
      const url = `/system-modules/${moduleSlug}/layouts/active${qs ? `?${qs}` : ''}`;
      const res = await api.get<ActiveLayoutResponse>(url);
      return res.data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!moduleSlug && options?.enabled !== false,
  });

  // Migracja w locie jeśli backend zwrócił stary kształt.
  // Memoizacja po surowej odpowiedzi — funkcja czysta, deterministyczna.
  const layoutConfig = useMemo<LayoutConfig | null>(() => {
    const raw = query.data?.layout?.layoutConfig;
    if (!raw) return null;
    const base = isAlreadyNewShape(raw)
      ? (raw as LayoutConfig)
      : (migrateLayoutToRecursive(raw) as LayoutConfig);
    // Nakładka per-tenant (ukrycia/kolejność zakładek na layoucie systemowym) —
    // dokładana na odczycie, żeby renderer karty (honorujący hidden/sortOrder)
    // nie wymagał zmian. Brak nakładki → ten sam obiekt (stabilność memo).
    return applyTabOverrides(base, query.data?.layout?.tabOverrides ?? null);
  }, [query.data?.layout?.layoutConfig, query.data?.layout?.tabOverrides]);

  return {
    layoutConfig,
    myRoleId: query.data?.myRoleId ?? null,
    isSuperUser: query.data?.isSuperUser ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    sourceViewSlug: query.data?.layout?.sourceViewSlug ?? null,
    previewDockEnabled: query.data?.layout?.previewDockEnabled ?? false,
  };
}
